/* eslint react-hooks/exhaustive-deps: "warn" */
import { useCallback, useEffect, useMemo } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';
import { useRouter } from 'libs/router/useRouter';
import { content } from 'libs/http/api/content/content';
import type { Page } from 'libs/http/api/common/common.types';

import { useRequest } from 'hooks/useRequest';

import { Icon } from 'ui/atoms/Icon/Icon';

import { filterColumnsByLocale, isRouteAvailable } from 'features/layout/routeLocaleAvailability';

import styles from './HeaderMenu.module.scss';

import { HeaderMenuDropdown } from '../HeaderMenuDropdown/HeaderMenuDropdown';
import type { ItemMenuInterface } from '../Header/Header.types';
import { CompareColumn } from './CompareColumn';
import type { MenuColumn } from './HeaderMenu.types';
import { MenuColumns } from './MenuColumns';
// OurDataSidebar ('Built for procurement') temporarily hidden — see render below.
// import { OurDataSidebar } from './OurDataSidebar';
import { SolutionsSidebar } from './SolutionsSidebar';
import { useLocalizedMenuData } from './useLocalizedMenuData';

const Div = (props: any) => <div {...props} />;

export const useHeaderMenu = () => {
  const router = useRouter();
  const lang = (router.query.lang as string) || 'en';

  const menuData = useLocalizedMenuData();

  const { loading: loadingPosts, request: requestPosts } = useRequest<Page[]>({ data: [] });

  useEffect(() => {
    requestPosts(content.articlesList({ lang: router.query.lang as string, page_size: 3, case_studies_type: '' }));
  }, [requestPosts, router.query.lang]);

  const ItemMenu = useCallback(
    (
      { loading, title, className = '', route = '', query, children, render, sidebar, wide }: ItemMenuInterface,
      key: number,
    ) => {
      const Component = route ? Link : Div;

      const hasActiveRoute =
        route === router.query.category ||
        children?.reduce((acc, current) => {
          if (
            !acc &&
            ((router.query.category !== undefined && current.route === router.query.category) ||
              current.children?.find((child) => child.route === router.query.category))
          ) {
            return true;
          }
          return acc;
        }, false) === true;

      if (children) {
        return (
          <div key={key} className={cn('header__menu-item', hasActiveRoute && 'active')}>
            <HeaderMenuDropdown label={title} loading={loading} sidebar={sidebar} wide={wide}>
              {(onClose) => {
                if (render) return render({ onCloseMenu: onClose });

                return (
                  <div className={cn('rc-dropdown-options-row', className)}>
                    {children.map(
                      ({ value, icon, title: itemTitle, description, route: itemRoute, query: itemQuery }) => {
                        const ItemComponent = itemRoute ? Link : Div;

                        return (
                          <ItemComponent
                            key={value ?? itemRoute}
                            className="rc-dropdown-options-item"
                            {...(itemRoute ? { route: itemRoute, query: itemQuery } : {})}
                            onClick={onClose}
                          >
                            {icon && (
                              <div className="rc-dropdown-options-item-icon">
                                <Icon type={icon} />
                              </div>
                            )}
                            {itemTitle}
                            {description && <div className="rc-dropdown-options-item-description">{description}</div>}
                          </ItemComponent>
                        );
                      },
                    )}
                  </div>
                );
              }}
            </HeaderMenuDropdown>
          </div>
        );
      }

      return (
        <Component
          {...(route ? { route, query } : {})}
          key={key}
          className={cn(
            'header__menu-item',
            loading && 'loading',
            hasActiveRoute && 'active',
            route && 'header-menu-link',
          )}
        >
          <span className="header-menu-link__label">{title}</span>
        </Component>
      );
    },
    [router.query],
  );

  const menu = useMemo(() => {
    // Hide a dropdown tab whose every link is filtered out in the active locale (e.g. Resources
    // in fr, where all items are non-localized CMS/blog pages) — an empty dropdown reads as broken.
    // Same intent as the isRouteAvailable() gate on the single-link Regis tab below.
    const hasColumns = (columns: MenuColumn[]) => filterColumnsByLocale(columns, lang).length > 0;

    return [
      ...(hasColumns(menuData.solutions.columns as MenuColumn[])
        ? [
            {
              title: menuData.solutions.title,
              value: 'solutions',
              wide: true,
              children: [],
              render: ({ onCloseMenu }: { onCloseMenu: () => void }) => (
                <div className={styles.platformColumns}>
                  <MenuColumns
                    columns={menuData.solutions.columns as MenuColumn[]}
                    columnClassName={styles.platformColumn}
                    onCloseMenu={onCloseMenu}
                  />

                  <SolutionsSidebar />
                </div>
              ),
            },
          ]
        : []),
      ...(hasColumns(menuData.whoWeServe.columns as MenuColumn[])
        ? [
            {
              title: menuData.whoWeServe.title,
              value: 'whoWeServe',
              children: [],
              render: ({ onCloseMenu }: { onCloseMenu: () => void }) => (
                <div className={styles.platformColumns}>
                  <MenuColumns
                    columns={menuData.whoWeServe.columns as MenuColumn[]}
                    columnClassName={styles.platformColumn}
                    onCloseMenu={onCloseMenu}
                  />
                </div>
              ),
            },
          ]
        : []),
      ...(isRouteAvailable(menuData.regis.navigation, lang)
        ? [
            {
              title: menuData.regis.title,
              route: menuData.regis.navigation,
            },
          ]
        : []),
      ...(hasColumns(menuData.ourData.columns as MenuColumn[])
        ? [
            {
              title: menuData.ourData.title,
              value: 'ourData',
              children: [],
              render: ({ onCloseMenu }: { onCloseMenu: () => void }) => {
                const columns = menuData.ourData.columns as MenuColumn[];
                const compareColumn = columns.find((column) =>
                  column.items.every((item) => item.navigation?.startsWith('global-database-vs-')),
                );
                const restColumns = columns.filter((column) => column !== compareColumn);

                return (
                  <div className={styles.platformColumns}>
                    <MenuColumns
                      columns={restColumns}
                      columnClassName={styles.platformColumn}
                      onCloseMenu={onCloseMenu}
                    />

                    {compareColumn && <CompareColumn column={compareColumn} onCloseMenu={onCloseMenu} />}

                    {/*
                      'Built for procurement' sidebar temporarily hidden — replaced by the
                      featured-style "Compare" column above. Kept for future reuse.
                      <OurDataSidebar />
                    */}
                  </div>
                );
              },
            },
          ]
        : []),
      ...(hasColumns(menuData.resources.columns as MenuColumn[])
        ? [
            {
              title: menuData.resources.title,
              value: 'resources',
              children: [],
              render: ({ onCloseMenu }: { onCloseMenu: () => void }) => (
                <div className={styles.platformColumns}>
                  <MenuColumns
                    columns={menuData.resources.columns as MenuColumn[]}
                    columnClassName={styles.platformColumn}
                    onCloseMenu={onCloseMenu}
                  />
                </div>
              ),
            },
          ]
        : []),
    ];
  }, [lang, menuData]);

  return { menu, ItemMenu, loadingPosts };
};
